import { registerOverlay } from "klinecharts";
let registered = false;
const figPolyline = (pts, styles = {}) => ({
  type: "line",
  attrs: { coordinates: pts },
  styles: { style: "solid", size: 2, color: "#111", ...styles },
});
const mkKiteHandleBase = (p) => ([
  {
    type: "circle",
    attrs: { x: p.x, y: p.y, r: 10 },
    styles: { style: "fill", fillColor: "#fff", borderColor: "#ddd", borderSize: 1 },
  }
]);
const mkRotateHandleIcon = (p) => {
  const base = mkKiteHandleBase(p);
  const arc = [
    { x: p.x - 4, y: p.y + 2 },
    { x: p.x - 6, y: p.y - 2 },
    { x: p.x - 2, y: p.y - 6 },
    { x: p.x + 4, y: p.y - 4 },
  ];
  const head = [
    { x: p.x + 4, y: p.y - 4 },
    { x: p.x + 1, y: p.y - 7 },
  ];
  return [
    ...base,
    figPolyline(arc, { size: 2, color: "#111" }),
    figPolyline(head, { size: 2, color: "#111" }),
  ];
};
const mkResizeHandleIcon = (p) => {
  const base = mkKiteHandleBase(p);
  const diag = [
    { x: p.x - 5, y: p.y + 5 },
    { x: p.x + 5, y: p.y - 5 },
  ];
  const head1 = [
    { x: p.x - 5, y: p.y + 5 },
    { x: p.x - 5, y: p.y + 1 },
  ];
  const head2 = [
    { x: p.x - 5, y: p.y + 5 },
    { x: p.x - 1, y: p.y + 5 },
  ];
  const head3 = [
    { x: p.x + 5, y: p.y - 5 },
    { x: p.x + 5, y: p.y - 1 },
  ];
  const head4 = [
    { x: p.x + 5, y: p.y - 5 },
    { x: p.x + 1, y: p.y - 5 },
  ];
  return [
    ...base,
    figPolyline(diag, { size: 2, color: "#111" }),
    figPolyline(head1, { size: 2, color: "#111" }),
    figPolyline(head2, { size: 2, color: "#111" }),
    figPolyline(head3, { size: 2, color: "#111" }),
    figPolyline(head4, { size: 2, color: "#111" }),
  ];
};
export function ensureDrawOverlaysRegistered() {
  if (registered) return;
  registered = true;
  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
  const rectFrom2 = (a, b) => {
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    const width = Math.abs(a.x - b.x);
    const height = Math.abs(a.y - b.y);
    return { x, y, width, height };
  };
  const figLine = (p1, p2, styles = {}) => ({
    type: "line",
    attrs: { coordinates: [{ x: p1.x, y: p1.y }, { x: p2.x, y: p2.y }] },
    styles: { style: "solid", size: 2, color: "#111", ...styles },
  });
  const figText = (x, y, text, styles = {}) => ({
    type: "text",
    attrs: { x, y, text: text ?? "", align: "left", baseline: "middle" },
    styles: { style: "fill", color: "#111", size: 12, ...styles },
  });
  const localToWorld = (center, pt, scale, angle) => {
    const cos = Math.cos(angle), sin = Math.sin(angle);
    const x = pt.x * scale;
    const y = pt.y * scale;
    return { x: center.x + (x * cos - y * sin), y: center.y + (x * sin + y * cos) };
  };
  const mkHandle = (p, border = "#111") => ({
    type: "circle",
    attrs: { x: p.x, y: p.y, r: 5 },
    styles: { style: "fill", color: "#fff", borderColor: border, borderSize: 1 },
  });
  registerOverlay({
    name: "kiteRect",
    totalStep: 2,
    needDefaultPointFigure: true,
    createPointFigures: ({ coordinates }) => {
      const [a, b] = coordinates || [];
      if (!a || !b) return [];
      return [{
        type: "rect",
        attrs: rectFrom2(a, b),
        styles: { style: "stroke", borderSize: 2, borderColor: "#111" },
      }];
    },
  });
  registerOverlay({
    name: "kiteEllipse",
    totalStep: 2,
    needDefaultPointFigure: true,
    createPointFigures: ({ coordinates, overlay }) => {
      const [a, b] = coordinates || [];
      if (!a || !b) return [];
      const r = rectFrom2(a, b);
      const cx = r.x + r.width / 2;
      const cy = r.y + r.height / 2;
      const rx = Math.max(2, r.width / 2);
      const ry = Math.max(2, r.height / 2);
      const borderColor = overlay?.styles?.borderColor || "#e53935";
      const fillColor = overlay?.styles?.fillColor || "rgba(30, 136, 229, 0)";
      const N = 64;
      const pts = [];
      for (let i = 0; i < N; i++) {
        const t = (i / N) * Math.PI * 2;
        pts.push({ x: cx + rx * Math.cos(t), y: cy + ry * Math.sin(t) });
      }
      return [
        {
          type: "polygon",
          attrs: { coordinates: pts },
          styles: {
            style: "stroke",
            borderColor,
            borderSize: 2,
          },
        },
      ];
    },
  });
  registerOverlay({
    name: "kiteAnnotation",
    totalStep: 1,
    needDefaultPointFigure: true,
    createPointFigures: ({ coordinates, overlay }) => {
      const [p] = coordinates || [];
      if (!p) return [];
      const text = String(overlay?.extendData?.text || "");
      return [figText(p.x, p.y, text, { baseline: "middle" })];
    },
  });
  registerOverlay({
    name: "kiteCross",
    totalStep: 1,
    needDefaultPointFigure: true,
    createPointFigures: ({ coordinates, bounding }) => {
      const [p] = coordinates || [];
      if (!p) return [];
      return [
        { type: "line", attrs: { coordinates: [{ x: bounding.left, y: p.y }, { x: bounding.right, y: p.y }] }, styles: { style: "solid", size: 1, color: "#111" } },
        { type: "line", attrs: { coordinates: [{ x: p.x, y: bounding.top }, { x: p.x, y: bounding.bottom }] }, styles: { style: "solid", size: 1, color: "#111" } },
      ];
    },
  });
  registerOverlay({
    name: "kiteCrosslineTag",
    totalStep: 1,
    lock: true,
    visible: true,
    zLevel: 10000,
    needDefaultPointFigure: false,

    createPointFigures: ({ coordinates, overlay, bounding }) => {
      const c = coordinates?.[0];
      if (!c || !bounding) return [];
      const rightW = 70;
      const bottomH = 26;
      const priceText =
        overlay.extendData?.priceText ??
        (overlay.points?.[0]?.value != null ? Number(overlay.points[0].value).toFixed(2) : "");
      const timeText =
        overlay.extendData?.timeText ??
        (overlay.points?.[0]?.timestamp
          ? new Date(overlay.points[0].timestamp).toISOString().slice(0, 16).replace("T", " ")
          : "");
      const ph = 20;
      const px = Math.max(0, bounding.width - rightW + 2);
      const pw = Math.max(0, rightW - 4);
      const py = c.y - ph / 2;
      const th = 20;
      const tw = Math.max(70, String(timeText).length * 7 + 16);
      const ty = Math.max(0, bounding.height - th - 6);
      const leftLimit = 2;
      const rightLimit = Math.max(leftLimit, bounding.width - rightW - 2 - tw);
      const tx = Math.max(leftLimit, Math.min(c.x - tw / 2, rightLimit));
      const boxStyle = {
        style: "fill",
        color: "rgba(60,60,60,0.85)",
        borderColor: "rgba(255,255,255,0.25)",
        borderSize: 1,
        borderRadius: 4,
      };
      const textStyle = {
        color: "#ffffff",
        size: 12,
      };
      return [
        { type: "rect", attrs: { x: px, y: py, width: pw, height: ph }, styles: boxStyle },
        { type: "text", attrs: { x: px + pw / 2, y: py + ph / 2, text: String(priceText), align: "center", baseline: "middle" }, styles: textStyle },
        { type: "rect", attrs: { x: tx, y: ty, width: tw, height: th }, styles: boxStyle },
        { type: "text", attrs: { x: tx + tw / 2, y: ty + th / 2, text: String(timeText), align: "center", baseline: "middle" }, styles: textStyle },
      ];
    },
  });
  registerOverlay({
    name: "kiteContinuous",
    totalStep: 9999,
    needDefaultPointFigure: true,
    createPointFigures: ({ coordinates }) => {
      const pts = coordinates || [];
      if (pts.length < 2) return [];
      const out = [];
      for (let i = 0; i < pts.length - 1; i++) out.push(figLine(pts[i], pts[i + 1], { color: "#111", size: 2 }));
      return out;
    },
  });
  registerOverlay({
    name: "kiteDoodle",
    totalStep: 2,
    needDefaultPointFigure: false,
    createPointFigures: ({ coordinates }) => {
      const pts = coordinates || [];
      if (pts.length < 2) return [];
      return [{
        type: "line",
        attrs: { coordinates: pts },
        styles: { style: "solid", size: 2, color: "#111" },
      }];
    },
  });
  registerOverlay({
    name: "kiteArrowSticker",
    totalStep: 1,
    needDefaultPointFigure: false,
    createPointFigures: ({ coordinates, overlay }) => {
      const [p] = coordinates || [];
      if (!p) return [];
      const ex = overlay?.extendData || {};
      const showTools = !!ex.showTools;
      const scale = Number(ex.scale ?? 1);
      const angle = Number(ex.angle ?? 0);
      const border = "#111";
      const center = { x: p.x, y: p.y };
      const base = [
        { x: 0, y: -42 }, { x: 26, y: -10 }, { x: 10, y: -10 },
        { x: 10, y: 42 }, { x: -10, y: 42 }, { x: -10, y: -10 }, { x: -26, y: -10 },
      ];
      const poly = base.map(pt => localToWorld(center, pt, scale, angle));
      const out = [
        { type: "polygon", attrs: { coordinates: poly }, styles: { style: "stroke", borderColor: border, borderSize: 2 } },
      ];
      if (showTools) {
        const rotatePt = localToWorld(center, { x: -55, y: -10 }, scale, angle);
        const resizePt = localToWorld(center, { x: -55, y: 30 }, scale, angle);
        out.push(
          {
            type: "circle",
            attrs: { x: center.x, y: center.y, r: 6 },
            styles: { style: "fill", fillColor: "#fff", borderColor: border, borderSize: 2 },
          },
          figPolyline(
            [{ x: center.x - 10, y: center.y }, { x: center.x + 10, y: center.y }],
            { size: 1, color: border }
          ),
          figPolyline(
            [{ x: center.x, y: center.y - 10 }, { x: center.x, y: center.y + 10 }],
            { size: 1, color: border }
          ),
          ...mkRotateHandleIcon(rotatePt),
          ...mkResizeHandleIcon(resizePt),
        );
      }
      return out;
    },
  });
  registerOverlay({
    name: "kiteSticker",
    totalStep: 1,
    needDefaultPointFigure: false,
    createPointFigures: ({ coordinates, overlay }) => {
      const [p] = coordinates || [];
      if (!p) return [];
      const ex = overlay?.extendData || {};
      const showTools = !!ex.showTools;
      const kind = String(ex.kind || "check");
      const scale = Number(ex.scale ?? 1);
      const angle = Number(ex.angle ?? 0);
      const blue = "#1e88e5";
      const center = { x: p.x, y: p.y };
      const out = [];
      if (kind === "check") {
        const a = localToWorld(center, { x: -28, y: 0 }, scale, angle);
        const b = localToWorld(center, { x: -6, y: 22 }, scale, angle);
        const c = localToWorld(center, { x: 30, y: -22 }, scale, angle);
        out.push({
          type: "line",
          attrs: { coordinates: [a, b, c] },
          styles: { style: "solid", size: 10, color: blue },
        });
      } else if (kind === "x") {
        const a1 = localToWorld(center, { x: -26, y: -26 }, scale, angle);
        const a2 = localToWorld(center, { x: 26, y: 26 }, scale, angle);
        const b1 = localToWorld(center, { x: -26, y: 26 }, scale, angle);
        const b2 = localToWorld(center, { x: 26, y: -26 }, scale, angle);
        out.push(
          { type: "line", attrs: { coordinates: [a1, a2] }, styles: { style: "solid", size: 10, color: blue } },
          { type: "line", attrs: { coordinates: [b1, b2] }, styles: { style: "solid", size: 10, color: blue } },
        );
      }
      if (showTools) {
        const rotatePt = localToWorld(center, { x: -55, y: -10 }, scale, angle);
        const resizePt = localToWorld(center, { x: -55, y: 30 }, scale, angle);
        out.push(
          {
            type: "circle",
            attrs: { x: center.x, y: center.y, r: 6 },
            styles: { style: "fill", fillColor: "#fff", borderColor: "#111", borderSize: 2 },
          },
          figPolyline(
            [{ x: center.x - 10, y: center.y }, { x: center.x + 10, y: center.y }],
            { size: 1, color: "#111" }
          ),
          figPolyline(
            [{ x: center.x, y: center.y - 10 }, { x: center.x, y: center.y + 10 }],
            { size: 1, color: "#111" }
          ),
          ...mkRotateHandleIcon(rotatePt),
          ...mkResizeHandleIcon(resizePt),
        );
      }
      return out;
    },
  });

}
